import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, attendanceAPI, taskAPI, leaveAPI } from '../services/api';
import './Dashboard.css';

/* ── Helpers ── */
const fmt = (t) => t ? new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';
const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const today = () => new Date().toISOString().split('T')[0];

/* ══════════════════════════════════════════════
   EMPLOYEE PERSONAL DASHBOARD
══════════════════════════════════════════════ */
const EmployeeDashboard = ({ currentUser }) => {
  const [todayAtt, setTodayAtt]     = useState(null);
  const [attSummary, setAttSummary] = useState(null);
  const [attRecent, setAttRecent]   = useState([]);
  const [tasks, setTasks]           = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [todayRes, myAttRes, tasksRes] = await Promise.allSettled([
          attendanceAPI.getToday(),
          attendanceAPI.getMy({ from: monthStart(), to: today(), limit: 30 }),
          taskAPI.getAll(),
        ]);

        if (todayRes.status === 'fulfilled') setTodayAtt(todayRes.value);
        if (myAttRes.status === 'fulfilled') {
          setAttSummary(myAttRes.value?.summary || null);
          setAttRecent((myAttRes.value?.data || []).slice(0, 7));
        }
        if (tasksRes.status === 'fulfilled') {
          setTasks(tasksRes.value?.data || []);
        }

        // Leave balance — try with user _id
        if (currentUser?._id) {
          try {
            const lb = await leaveAPI.getBalance(currentUser._id);
            setLeaveBalance(lb?.data || lb || null);
          } catch {}
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px', color: '#9ca3af' }}>
        <i className="fas fa-spinner fa-spin" style={{ fontSize: 32 }}></i>
        <p style={{ marginTop: 14, fontSize: 15 }}>Loading your dashboard...</p>
      </div>
    );
  }

  /* ── Derive values ── */
  const checkedIn  = todayAtt?.checkedIn  || false;
  const checkedOut = todayAtt?.checkedOut || false;
  const checkInTime = todayAtt?.data?.checkIn;
  const checkOutTime = todayAtt?.data?.checkOut;
  const todayHours  = todayAtt?.data?.workHours;

  const presentDays = attSummary?.presentDays ?? '—';
  const totalHours  = attSummary?.totalHours  ?? '—';

  const myTasks      = tasks;
  const pendingTasks = myTasks.filter(t => t.status === 'pending' || t.status === 'in-progress').length;
  const doneTasks    = myTasks.filter(t => t.status === 'completed').length;

  const statusColor = {
    present:    { bg: '#d1fae5', color: '#065f46' },
    late:       { bg: '#fef3c7', color: '#92400e' },
    'half-day': { bg: '#fed7aa', color: '#9a3412' },
    absent:     { bg: '#fee2e2', color: '#991b1b' },
    'on-leave': { bg: '#dbeafe', color: '#1e40af' },
  };
  const getBadge = (s) => statusColor[(s || '').toLowerCase()] || { bg: '#f3f4f6', color: '#374151' };

  const taskStatusColor = {
    pending:      { bg: '#fef9c3', color: '#854d0e' },
    'in-progress':{ bg: '#dbeafe', color: '#1e40af' },
    'on-hold':    { bg: '#f3e8ff', color: '#6b21a8' },
    completed:    { bg: '#d1fae5', color: '#065f46' },
    cancelled:    { bg: '#fee2e2', color: '#991b1b' },
  };
  const getTaskBadge = (s) => taskStatusColor[(s || '').toLowerCase()] || { bg: '#f3f4f6', color: '#374151' };

  /* ── Attendance chart data ── */
  const attChartData = [...attRecent].reverse().map(r => ({
    date: fmtDate(r.date),
    hours: r.workHours || 0,
  }));

  return (
    <div className="dashboard">
      {/* ── Welcome Banner ── */}
      <div className="emp-welcome-banner">
        <div className="emp-welcome-left">
          <h2>Welcome back, {currentUser?.name?.split(' ')[0] || 'there'} 👋</h2>
          <p>{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
        <div className="emp-today-attendance">
          {!checkedIn && (
            <span className="emp-att-status emp-att-status--notIn">
              <i className="fas fa-circle-xmark"></i> Aaj login nahi kiya
            </span>
          )}
          {checkedIn && !checkedOut && (
            <span className="emp-att-status emp-att-status--in">
              <i className="fas fa-circle-check"></i> Logged in at {fmt(checkInTime)}
            </span>
          )}
          {checkedIn && checkedOut && (
            <span className="emp-att-status emp-att-status--done">
              <i className="fas fa-check-double"></i> Day complete · {todayHours ? todayHours + 'h worked' : fmt(checkInTime) + ' – ' + fmt(checkOutTime)}
            </span>
          )}
        </div>
      </div>

      {/* ── Stat Cards ── */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green"><i className="fas fa-calendar-check"></i></div>
          <div className="stat-info">
            <h3>{presentDays}</h3>
            <p>Present This Month</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue"><i className="fas fa-clock"></i></div>
          <div className="stat-info">
            <h3>{totalHours !== '—' ? totalHours + 'h' : '—'}</h3>
            <p>Hours This Month</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange"><i className="fas fa-tasks"></i></div>
          <div className="stat-info">
            <h3>{pendingTasks}</h3>
            <p>Pending Tasks</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green"><i className="fas fa-check-circle"></i></div>
          <div className="stat-info">
            <h3>{doneTasks}</h3>
            <p>Tasks Completed</p>
          </div>
        </div>
      </div>

      <div className="dashboard-charts">
        {/* ── Attendance Hours Chart ── */}
        {attChartData.length > 0 && (
          <div className="chart-card full-width">
            <h3><i className="fas fa-chart-bar"></i> My Attendance This Month (Work Hours)</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={attChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 12 }} />
                <YAxis stroke="#6b7280" />
                <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                <Bar dataKey="hours" name="Work Hours" fill="#6366f1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* ── My Recent Tasks ── */}
        <div className="chart-card">
          <h3><i className="fas fa-tasks"></i> My Tasks</h3>
          {myTasks.length === 0 ? (
            <div className="emp-empty">No tasks assigned</div>
          ) : (
            <div className="emp-task-list">
              {myTasks.slice(0, 8).map(t => {
                const tb = getTaskBadge(t.status);
                return (
                  <div key={t._id} className="emp-task-item">
                    <div className="emp-task-title">{t.title}</div>
                    <div className="emp-task-meta">
                      <span className="emp-mini-badge" style={{ background: tb.bg, color: tb.color }}>{t.status}</span>
                      {t.dueDate && <span className="emp-due">Due {fmtDate(t.dueDate)}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── My Recent Attendance ── */}
        <div className="chart-card">
          <h3><i className="fas fa-calendar-alt"></i> My Recent Attendance</h3>
          {attRecent.length === 0 ? (
            <div className="emp-empty">No attendance records</div>
          ) : (
            <table className="emp-att-table">
              <thead>
                <tr><th>Date</th><th>In</th><th>Out</th><th>Hrs</th><th>Status</th></tr>
              </thead>
              <tbody>
                {attRecent.map(r => {
                  const b = getBadge(r.status);
                  return (
                    <tr key={r._id}>
                      <td>{fmtDate(r.date)}</td>
                      <td>{fmt(r.checkIn)}</td>
                      <td>{fmt(r.checkOut)}</td>
                      <td>{r.workHours != null ? r.workHours + 'h' : '—'}</td>
                      <td><span className="emp-mini-badge" style={{ background: b.bg, color: b.color }}>{r.status || '—'}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Leave Balance ── */}
        {leaveBalance && (
          <div className="chart-card">
            <h3><i className="fas fa-umbrella-beach"></i> My Leave Balance</h3>
            <div className="emp-leave-grid">
              {Object.entries(leaveBalance).map(([key, val]) => (
                typeof val === 'number' ? (
                  <div key={key} className="emp-leave-card">
                    <div className="emp-leave-val">{val}</div>
                    <div className="emp-leave-lbl">{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</div>
                  </div>
                ) : null
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .emp-welcome-banner {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          border-radius: 16px;
          padding: 24px 28px;
          color: white;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 24px;
        }
        .emp-welcome-banner h2 { margin: 0; font-size: 22px; font-weight: 700; }
        .emp-welcome-banner p  { margin: 4px 0 0; opacity: 0.85; font-size: 14px; }
        .emp-today-attendance  { display: flex; align-items: center; }
        .emp-att-status {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 30px;
          font-size: 14px;
          font-weight: 600;
          white-space: nowrap;
        }
        .emp-att-status--notIn { background: rgba(239,68,68,0.25); color: #fecaca; }
        .emp-att-status--in    { background: rgba(16,185,129,0.25); color: #a7f3d0; }
        .emp-att-status--done  { background: rgba(255,255,255,0.2); color: #fff; }
        .emp-task-list { display: flex; flex-direction: column; gap: 10px; }
        .emp-task-item { display: flex; flex-direction: column; gap: 5px; padding: 10px 12px; background: #f8fafc; border-radius: 8px; border-left: 3px solid #6366f1; }
        .emp-task-title { font-size: 14px; font-weight: 500; color: #111827; }
        .emp-task-meta { display: flex; align-items: center; gap: 10px; }
        .emp-due { font-size: 12px; color: #9ca3af; }
        .emp-mini-badge { padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; text-transform: capitalize; }
        .emp-att-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .emp-att-table th { text-align: left; padding: 8px 10px; background: #f8fafc; color: #6b7280; font-size: 11px; font-weight: 700; text-transform: uppercase; border-bottom: 1px solid #e5e7eb; }
        .emp-att-table td { padding: 9px 10px; border-bottom: 1px solid #f1f5f9; color: #374151; }
        .emp-att-table tr:last-child td { border-bottom: none; }
        .emp-empty { text-align: center; color: #9ca3af; padding: 32px; font-size: 14px; }
        .emp-leave-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)); gap: 12px; }
        .emp-leave-card { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 14px 12px; text-align: center; }
        .emp-leave-val  { font-size: 24px; font-weight: 700; color: #059669; }
        .emp-leave-lbl  { font-size: 11px; color: #6b7280; margin-top: 4px; font-weight: 500; }
      `}</style>
    </div>
  );
};

/* ══════════════════════════════════════════════
   ADMIN / HR COMPANY DASHBOARD
══════════════════════════════════════════════ */
const AdminDashboard = () => {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardAPI.getStats()
      .then(res => setStats(res?.data || res || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Fallback static charts when API data not available
  const attendanceData = stats?.attendanceTrend || [];

  const departments = (stats?.employees?.byDepartment || []).map(d => ({
    name: d._id || 'Unknown',
    count: d.count,
  }));

  const leaveData = [
    { name: 'Approved', value: stats?.leaves?.approvedThisMonth || 0, color: '#10b981' },
    { name: 'Pending',  value: stats?.leaves?.pending           || 0, color: '#f59e0b' },
  ];

  const statCards = [
    { icon: 'fa-users',          count: stats?.employees?.total         ?? '—', label: 'Total Employees', color: 'blue' },
    { icon: 'fa-user-check',     count: stats?.attendance?.todayPresent ?? '—', label: 'Present Today',   color: 'green' },
    { icon: 'fa-user-times',     count: stats?.attendance?.todayAbsent  ?? '—', label: 'Absent Today',    color: 'red' },
    { icon: 'fa-calendar-minus', count: stats?.attendance?.todayOnLeave ?? '—', label: 'On Leave',        color: 'orange' },
    { icon: 'fa-tasks',          count: stats?.tasks?.pending           ?? '—', label: 'Pending Tasks',   color: 'blue' },
    { icon: 'fa-check-circle',   count: stats?.tasks?.completed         ?? '—', label: 'Tasks Done',      color: 'green' },
    { icon: 'fa-hourglass-half', count: stats?.leaves?.pending          ?? '—', label: 'Leave Requests',  color: 'orange' },
    { icon: 'fa-user-plus',      count: stats?.employees?.newThisMonth  ?? '—', label: 'New This Month',  color: 'green' },
  ];

  return (
    <div className="dashboard">
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 28 }}></i>
          <p style={{ marginTop: 12 }}>Loading dashboard...</p>
        </div>
      )}

      {!loading && (
        <>
          <div className="stats-grid">
            {statCards.map((stat, i) => (
              <div key={i} className="stat-card">
                <div className={`stat-icon ${stat.color}`}>
                  <i className={`fas ${stat.icon}`}></i>
                </div>
                <div className="stat-info">
                  <h3>{stat.count}</h3>
                  <p>{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="dashboard-charts">
            {/* Attendance Trend */}
            {attendanceData.length > 0 && (
              <div className="chart-card full-width">
                <h3><i className="fas fa-chart-line"></i> Attendance Trend (Last 7 Days)</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={attendanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                    <Legend />
                    <Line type="monotone" dataKey="present" stroke="#10b981" strokeWidth={2} name="Present" dot={{ fill: '#10b981', r: 4 }} />
                    <Line type="monotone" dataKey="absent"  stroke="#ef4444" strokeWidth={2} name="Absent"  dot={{ fill: '#ef4444', r: 4 }} />
                    <Line type="monotone" dataKey="leave"   stroke="#f59e0b" strokeWidth={2} name="On Leave" dot={{ fill: '#f59e0b', r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Department Distribution */}
            {departments.length > 0 && (
              <div className="chart-card">
                <h3><i className="fas fa-chart-bar"></i> Department Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={departments}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                    <Bar dataKey="count" name="Employees" radius={[8, 8, 0, 0]}>
                      {departments.map((_, i) => {
                        const colors = ['#667eea','#10b981','#f59e0b','#ef4444','#3b82f6','#8b5cf6'];
                        return <Cell key={i} fill={colors[i % colors.length]} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Leave Status Pie */}
            {leaveData.some(l => l.value > 0) && (
              <div className="chart-card">
                <h3><i className="fas fa-chart-pie"></i> Leave Status</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={leaveData} cx="50%" cy="50%" outerRadius={90}
                      labelLine={false}
                      label={({ name, percent }) => percent > 0 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''}
                      dataKey="value">
                      {leaveData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="legend-list">
                  {leaveData.map((item, i) => (
                    <div key={i} className="legend-item">
                      <span className="legend-color" style={{ backgroundColor: item.color }}></span>
                      <span>{item.name}: {item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════
   ROOT DASHBOARD — role-based switcher
══════════════════════════════════════════════ */
const Dashboard = () => {
  const { currentUser, hasPermission } = useAuth();
  const isPrivileged = hasPermission('employee_view'); // HR / Admin / SuperAdmin

  if (isPrivileged) return <AdminDashboard />;
  return <EmployeeDashboard currentUser={currentUser} />;
};

export default Dashboard;


